import { createSelector } from 'reselect';
import permissionConstants from 'xm-constants/permissions';
import {
  getHasCompanySupervisorPermission,
  getIsCompanyAdmin,
  getPermissions,
} from 'selectors/loggedInUser';
import {
  hasPermission,
  hasAllPermissions,
  hasAnyPermission,
} from 'utils/permissions';

import {
  pruneItemsAndGroups,
  selectFeatureToggleMap,
  selectLoggedInUser,
  selectSubMenuId,
} from './util';

function getAdminNav(
  featureToggleMap,
  loggedInUser,
  subMenuId,
  isCompanyAdmin,
  hasCompanySupervisorPermission,
  permissions,
) {
  const { isSuperAdmin } = loggedInUser;

  const isClearRuntimeHistoryPageEnabled =
    featureToggleMap.ENABLE_CLEAR_RUNTIME_HISTORY_PAGE === 'true';

  const isImportHistoryPageEnabled =
    featureToggleMap.ENABLE_LEGACY_IMPORT_DATA === 'true';

  const isReactHolidaysPageEnabled = featureToggleMap.REACT_HOLIDAYS === 'true';

  const isLiveCallRoutingEnabled =
    featureToggleMap.LIVE_CALL_ROUTING_MVP === 'true';

  const navItemGroups = [
    featureToggleMap.ENABLE_ACCOUNT_SETTINGS === 'true' && {
      id: 'NAV_PRICING_PLAN',
      items: [
        {
          id: 'NAV_ACCOUNT_SETTINGS_PLAN_OVERVIEW',
          to: 'PlanOverviewPlace:none',
          permission: permission =>
            isCompanyAdmin ||
            hasPermission(
              permission,
              permissionConstants.VIEW_SCREEN_ORG_PLAN_OVERVIEW,
            ),
          isActive: () => subMenuId === 'PlanOverviewPlace',
          selectorId: 'PlanOverviewPlace',
          showInSettings: true,
        },
      ],
    },
    {
      id: 'NAV_COMPANY',
      items: [
        {
          id: 'NAV_COMPANIES',
          to: 'CocoonPlace:/xmatters/Admin/Companies/Companies.do',
          permission: permissionConstants.VIEW_MENUITEM_COMPANIES,
          isActive: () => subMenuId === 'Companies', // Match all subpages
          selectorId: 'Companies',
        },
        isSuperAdmin && {
          id: 'NAV_COMPANY_DETAILS',
          to: 'CocoonPlace:/xmatters/Admin/CompanyDetails/MyCompanyDetails.do',
          permission: permissionConstants.VIEW_MENUITEM_MY_COMPANY_DETAILS,
          isActive: () => subMenuId === 'CompanyDetails',
          selectorId: 'CompanyDetails',
        },
        {
          id: 'NAV_GENERAL_SETTINGS',
          to: 'GeneralSettingsPage',
          permission:
            permissionConstants.ABILITY_ACT_VIEW_BASIC_COMPANY_DETAILS,
          selectorId: 'BasicCompanyDetailsPlace',
          showInSettings: true,
        },
        {
          id: 'CUSTOM_USER_PROPERTIES',
          permission: permission =>
            isCompanyAdmin ||
            hasCompanySupervisorPermission ||
            hasPermission(
              permission,
              permissionConstants.ABILITY_ACT_MANAGE_USER_PROPERTIES,
            ),
          to: 'UserPropertiesPage',
          isActive: () => subMenuId === 'UserPropertiesPage',
          selectorId: 'UserPropertiesPage',
          showInSettings: true,
        },
      ],
    },
    {
      id: 'NAV_LOCATION',
      items: [
        {
          id: 'NAV_SITES',
          to: 'SitesPlace:sites',
          permission: permissionConstants.VIEW_MENUITEM_SITES,
          isActive: () => subMenuId === 'SitesPlace', // Match index, add, and edit routes
          selectorId: 'SitesPlace',
          showInSettings: true,
        },
        {
          id: 'NAV_LOCATIONS_AND_LANGUAGES',
          to: 'LocationsAndLanguages:all',
          permission: permission =>
            hasAnyPermission(
              permission,
              permissionConstants.VIEW_MENUITEM_COUNTRIES,
              permissionConstants.VIEW_MENUITEM_TIME_ZONES,
              permissionConstants.VIEW_MENUITEM_LANGUAGES,
            ),
          isActive: () => subMenuId === 'LocationsAndLanguages',
          selectorId: 'LocationsAndLanguages',
          showInSettings: true,
        },
      ],
    },
    {
      id: 'NAV_CALENDARS',
      items: [
        isReactHolidaysPageEnabled && {
          id: 'HOLIDAYS',
          to: 'Holidays',
          permission: permission =>
            hasAllPermissions(
              permission,
              permissionConstants.VIEW_MENUITEM_CUSTOM_HOLIDAYS,
              permissionConstants.VIEW_MENUITEM_COMPANY_HOLIDAYS,
            ),
          selectorId: 'Holidays',
          showInSettings: true,
        },
        !isReactHolidaysPageEnabled && {
          id: 'NAV_CUSTOM_HOLIDAYS',
          to: 'CocoonPlace:/xmatters/Admin/CustomHolidays/CustomHolidays.do',
          permission: permissionConstants.VIEW_MENUITEM_CUSTOM_HOLIDAYS,
          isActive: () => subMenuId === 'CustomHolidays', // Match index, add, and edit routes
          selectorId: 'CustomHolidays',
        },
        !isReactHolidaysPageEnabled && {
          id: 'NAV_COMPANY_HOLIDAYS',
          to: 'CocoonPlace:/xmatters/Admin/CompanyHolidays/CompanyHolidays.do',
          permission: permissionConstants.VIEW_MENUITEM_COMPANY_HOLIDAYS,
          isActive: () => subMenuId === 'CompanyHolidays', // Match index, add, and edit routes
          selectorId: 'CompanyHolidays',
        },
        !isReactHolidaysPageEnabled && {
          id: 'NAV_SITE_HOLIDAYS',
          to: 'CocoonPlace:/xmatters/Admin/SelectSiteForSiteHoliday/SelectSiteForSiteHoliday.do',
          permission: permissionConstants.VIEW_MENUITEM_COMPANY_HOLIDAYS,
          isActive: () => subMenuId === 'SelectSiteForSiteHoliday', // Cocoon messes with the URL when editing, compare subMenuId instead
          selectorId: 'SelectSiteForSiteHoliday',
        },
      ],
    },
    {
      id: 'NAV_PERMISSIONS',
      items: [
        // {
        //   id: 'NAV_ACTIVE_LICENSES',
        //   to: 'CocoonPlace:/xmatters/Admin/ActiveLicenses/Licenses.do',
        //   permission: permissionConstants.VIEW_MENUITEM_LICENSES,
        // },

        {
          id: 'NAV_FUNCTIONS',
          to: 'CocoonPlace:/xmatters/Admin/Roles/Roles.do',
          permission: permissionConstants.VIEW_MENUITEM_ROLES,
          isActive: () => subMenuId === 'Roles', // Cocoon messes with the URL when editing, compare subMenuId instead
          selectorId: 'roles',
        },
        {
          id: 'NAV_ROLES',
          to: 'CocoonPlace:/xmatters/Admin/AdministrativeGroups/Functions.do',
          permission: permissionConstants.VIEW_MENUITEM_FUNCTIONS,
          isActive: () => subMenuId === 'AdministrativeGroups', // Cocoon messes with the URL when adding/editting, compare subMenuId instead
          selectorId: 'AdministrativeGroups',
        },
        {
          id: 'NAV_EXTERNAL_DATA',
          to: 'ExternalDataPage',
          permission: permission =>
            isCompanyAdmin ||
            hasCompanySupervisorPermission ||
            hasPermission(
              permission,
              permissionConstants.VIEW_MENUITEM_EXTERNALDATA,
            ),

          isActive: () => subMenuId === 'ExternalDataPage',
          selectorId: 'ExternalData',
        },
        {
          id: 'NAV_INTEGRATION_AGENTS',
          to: 'CocoonPlace:/xmatters/Admin/IntegrationAgentAccessControl/IntegrationAgentAccessControl.do',
          permission:
            permissionConstants.VIEW_MENUITEM_INTEGRATION_AGENT_ACCESS_CONTROL,
          isActive: () => subMenuId === 'IntegrationAgentAccessControl', // Cocoon messes with the URL after clicking "continue"
          selectorId: 'IntegrationAgentAccessControl',
        },
      ],
    },
    {
      id: 'NAV_CONFIGURATION',
      items: [
        {
          id: 'NAV_GLOBAL_CONFIGURATION',
          to: 'CocoonPlace:/xmatters/Admin/GlobalParameters/GlobalParameters.do',
          permission: permissionConstants.VIEW_MENUITEM_GLOBAL_PARAMETERS,
          selectorId: 'GlobalConfiguration',
        },
        isImportHistoryPageEnabled && {
          id: 'NAV_IMPORT_DATA',
          to: 'CocoonPlace:/xmatters/Admin/ImportData/ImportData.do',
          permission: permissionConstants.VIEW_MENUITEM_IMPORT_DATA,
          isActive: () => subMenuId === 'ImportData', // Cocoon messes with the URL after clicking "upload"
          selectorId: 'ScheduleJobs',
        },
        {
          id: 'NAV_DEVICE_MANAGEMENT',
          to: 'DeviceManagementPlace:',
          permission: permission =>
            isCompanyAdmin ||
            hasPermission(
              permission,
              permissionConstants.ABILITY_ACT_MANAGE_DEVICE_NAMES,
            ),
          selectorId: 'DeviceManagementPlace',
          isActive: () => subMenuId === 'DeviceManagementPlace', // Match index, add, and edit routes
        },
        {
          id: 'SECURITY_SETTINGS',
          to: 'SecuritySettingsPage',
          permission: permission =>
            isCompanyAdmin ||
            hasCompanySupervisorPermission ||
            hasPermission(
              permission,
              permissionConstants.ABILITY_ACT_MANAGE_PASSWORD_POLICY,
            ),
          isActive: () => subMenuId === 'SecuritySettings', // Match all subpages
          selectorId: 'SecuritySettings',
          showInSettings: true,
        },
        isClearRuntimeHistoryPageEnabled && {
          id: 'NAV_CLEAR_RUNTIME_HISTORY',
          to: 'CocoonPlace:/xmatters/Admin/ClearRuntimeHistory/ClearRuntimeHistory.do',
          permission: permissionConstants.VIEW_MENUITEM_CLEAR_RUNTIME_HISTORY,
          selectorId: 'ClearRuntimeHistory',
        },
        isLiveCallRoutingEnabled && {
          id: 'NAV_LIVE_CALL_ROUTING',
          to: 'LiveCallRoutingPage',
          permission: permission =>
            isCompanyAdmin ||
            hasPermission(
              permission,
              permissionConstants.ABILITY_ACT_EDITLIVECALLROUTING,
            ),
          isActive: () => subMenuId === 'LiveCallRouting',
          selectorId: 'LiveCallRouting',
        },
        {
          id: 'NAV_SYSTEM_WORKFLOWS',
          to: 'SystemApplicationListPlace:null',
          permission: permissionConstants.VIEW_MENUITEM_SYSTEM_APPLICATIONS,
          isActive: () =>
            subMenuId === 'SystemApplicationListPlace' ||
            subMenuId === 'SystemSubscriptionFormListPlace', // Match all subpages
          selectorId: 'SystemApplicationListPlace',
        },
        {
          id: 'NAV_INTEGRATION_SERVICES',
          to: 'CocoonPlace:/xmatters/Admin/IntegrationServices/IntegrationServices.do',
          permission: permissionConstants.VIEW_MENUITEM_COMPANY_ADMINS,
          isActive: () =>
            subMenuId === 'IntegrationServices' ||
            subMenuId === 'IntegrationServiceDetails', // Match all subroutes
          selectorId: 'IntegrationServices',
        },
      ],
    },
    {
      id: 'NAV_PROVIDERS',
      items: [
        {
          id: 'NAV_USER_SERVICE_PROVIDERS',
          to: 'CocoonPlace:/xmatters/Admin/UserLevelProviders/UserLevelProviders.do',
          permission: permissionConstants.VIEW_MENUITEM_USER_LEVEL_PROVIDERS,
          isActive: () => subMenuId === 'UserLevelProviders', // Match all subpages
          selectorId: 'UserLevelProviders',
        },
        {
          id: 'NAV_PROTOCOL_LEVEL_PROVIDERS',
          to: 'CocoonPlace:/xmatters/Admin/ProtocolLevelProviders/ProtocolLevelProviders.do',
          permission:
            permissionConstants.VIEW_MENUITEM_PROTOCOL_LEVEL_PROVIDERS,
          isActive: () => subMenuId === 'ProtocolLevelProviders', // Match all subpages
          selectorId: 'ProtocolLevelProviders',
        },
      ],
    },
  ];

  return pruneItemsAndGroups(navItemGroups, permissions);
}

export default createSelector(
  [
    selectFeatureToggleMap,
    selectLoggedInUser,
    selectSubMenuId,
    getIsCompanyAdmin,
    getHasCompanySupervisorPermission,
    getPermissions,
  ],
  getAdminNav,
);
